import React, { useState, useEffect, useRef } from "react";
import { TextAa, CaretDown, Check } from "@phosphor-icons/react";

export const FONT_OPTIONS = [
  // Sprint 2 (02-2026) — Inter làm mặc định (đã bundle 400/700 trong index.css,
  // ~2 WOFF2 critical). Các family khác lazy-load qua ensureFontLoaded.
  { id: "inter", name: "Inter", stack: `"Inter", system-ui, sans-serif`, sample: "Giải đấu · Vô địch", css: null },
  { id: "be-vietnam-pro", name: "Be Vietnam Pro", stack: `"Be Vietnam Pro", sans-serif`, sample: "Giải đấu · Vô địch", css: "family=Be+Vietnam+Pro:wght@400;500;600;700;800" },
  { id: "unbounded", name: "Unbounded", stack: `"Unbounded", "Be Vietnam Pro", sans-serif`, sample: "Giải đấu · Vô địch", css: "family=Unbounded:wght@600;700;800;900" },
  { id: "nunito", name: "Nunito", stack: `"Nunito", sans-serif`, sample: "Giải đấu · Vô địch", css: "family=Nunito:wght@400;600;700;800" },
  { id: "montserrat", name: "Montserrat", stack: `"Montserrat", sans-serif`, sample: "Giải đấu · Vô địch", css: "family=Montserrat:wght@400;500;600;700;800" },
  { id: "manrope", name: "Manrope", stack: `"Manrope", sans-serif`, sample: "Giải đấu · Vô địch", css: "family=Manrope:wght@400;500;600;700;800" },
  { id: "mulish", name: "Mulish", stack: `"Mulish", sans-serif`, sample: "Giải đấu · Vô địch", css: "family=Mulish:wght@400;500;600;700;800" },
  { id: "barlow", name: "Barlow", stack: `"Barlow", sans-serif`, sample: "Giải đấu · Vô địch", css: "family=Barlow:wght@400;500;600;700" },
  { id: "lora", name: "Lora (Serif)", stack: `"Lora", serif`, sample: "Giải đấu · Vô địch", css: "family=Lora:wght@400;500;600;700" },
  { id: "noto-serif", name: "Noto Serif (Serif)", stack: `"Noto Serif", serif`, sample: "Giải đấu · Vô địch", css: "family=Noto+Serif:wght@400;600;700" },
];

const STORAGE_KEY = "bracket-ops-font";
// Sprint 2 (02-2026) — Chỉ Inter bundled critical; các family khác lazy khi
// user chọn hoặc mở menu FontSwitcher (giữ nguyên hành vi).
const _loadedFontIds = new Set(["inter"]);
const ensureFontLoaded = (font) => {
  if (!font || !font.css || _loadedFontIds.has(font.id)) return;
  _loadedFontIds.add(font.id);
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?${font.css}&display=swap`;
  link.setAttribute("data-lazy-font", font.id);
  document.head.appendChild(link);
};

export const applyFont = (stack) => {
  const root = document.documentElement;
  root.style.setProperty("--font-vn", stack);
  // Apply the chosen font to headings AND mono labels so the entire UI updates visibly.
  root.style.setProperty("--font-heading", stack);
  root.style.setProperty("--font-mono", stack);
};

export const getSavedFont = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const found = FONT_OPTIONS.find((f) => f.id === saved);
      if (found) {
        // Lazy-load ngay khi khôi phục lựa chọn cũ từ localStorage.
        ensureFontLoaded(found);
        return found;
      }
    }
  } catch (e) {}
  return FONT_OPTIONS[0];
};

export const FontSwitcher = () => {
  const [current, setCurrent] = useState(getSavedFont);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    applyFont(current.stack);
  }, [current]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (f) => {
    ensureFontLoaded(f);
    setCurrent(f);
    try {
      localStorage.setItem(STORAGE_KEY, f.id);
    } catch (e) {}
    setOpen(false);
  };

  // Khi user mở menu → preload toàn bộ font ngoài core để preview trong dropdown mượt.
  useEffect(() => {
    if (!open) return;
    FONT_OPTIONS.forEach(ensureFontLoaded);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#007AFF] text-white/70 hover:text-white font-mono uppercase text-[10px] tracking-[0.22em] transition-all duration-150"
        data-testid="font-switcher-btn"
        title="Chọn font Tiếng Việt"
      >
        <TextAa size={14} weight="bold" />
        <span className="hidden md:inline truncate max-w-[120px]" style={{ fontFamily: current.stack }}>
          {current.name}
        </span>
        <CaretDown size={10} weight="bold" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-72 bg-[#13161C] border border-white/15 z-50 shadow-2xl shadow-black/50"
          data-testid="font-switcher-menu"
        >
          <div className="px-4 py-3 border-b border-white/10">
            <div className="text-[10px] tracking-[0.28em] uppercase font-mono text-[#007AFF]">// Font Tiếng Việt</div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto py-2">
            {FONT_OPTIONS.map((f) => (
              <button
                key={f.id}
                onClick={() => pick(f)}
                className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-white/5 text-left transition ${
                  current.id === f.id ? "bg-[#007AFF]/10" : ""
                }`}
                data-testid={`font-option-${f.id}`}
                style={{ fontFamily: f.stack }}
              >
                <div className="w-4 pt-1 shrink-0">
                  {current.id === f.id && <Check size={14} weight="bold" color="#007AFF" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs tracking-wide uppercase font-mono text-white/60 mb-1">{f.name}</div>
                  <div className="text-base font-semibold text-white truncate">{f.sample}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
